import { FC, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { queryKeys } from '@constants';
import {
  captureComposerOrigin,
  ChatComposer,
  ChatSurface,
  clearComposerOrigin,
  createDraftConversationId,
  SuggestedQuestions,
  useChatAttachments,
} from '@features/chat';
import { komplyanceAiApiService } from '@features/komplyance-ai';
import { Trans, useTrans } from '@libs/i18n';
import { type KomplyanceAiWorkflowStartedChunk } from '@libs/types';

export const NewChatPage: FC = observer(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTrans();

  const [message, setMessage] = useState('');
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const composerShellRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLInputElement | null>(null);

  // There is no conversation to upload into yet, so documents attached here go to
  // a draft folder whose key travels with the first message. Held in a ref so the
  // whole hero session — attach, replace, send — files into one place.
  const draftConversationIdRef = useRef<string>(createDraftConversationId());
  const attachments = useChatAttachments({ conversationId: draftConversationIdRef.current });

  const handleToggleDeepResearch = useCallback(() => {
    setDeepResearchEnabled((prev) => !prev);
  }, []);

  const submit = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSubmitting) return;

      const { document } = attachments;

      setError(null);
      setIsSubmitting(true);
      // Hand the composer's current position to the thread we're about to open,
      // so it can fly the docked composer up from here.
      captureComposerOrigin(composerShellRef.current);

      try {
        await komplyanceAiApiService.sendMessage(
          { message: trimmed, deepResearch: deepResearchEnabled, document },
          (chunk: KomplyanceAiWorkflowStartedChunk) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversations });
            navigate(`/chat/${chunk.conversation_id}`, {
              state: {
                initialMessage: trimmed,
                initialDeepResearchEnabled: deepResearchEnabled,
                // Carried over so the first bubble shows its attachment straight
                // away, before the message list has been fetched back.
                initialFiles: document
                  ? [
                      {
                        fileName: document.fileName,
                        mimeType: document.mimeType,
                        docId: document.docId,
                        s3Key: document.s3Key,
                      },
                    ]
                  : undefined,
              },
            });
          },
        );
      } catch (err) {
        console.error('[NewChatPage] Failed to start conversation:', err);
        clearComposerOrigin();
        setError(err instanceof Error ? err.message : (t('chat.errors.unexpectedError') as string));
        setIsSubmitting(false);
      }
    },
    [attachments, deepResearchEnabled, isSubmitting, navigate, queryClient, t],
  );

  return (
    <ChatSurface backdrop="image" framed={false}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          overflowY: 'auto',
          '@keyframes heroIn': {
            from: { opacity: 0, transform: 'translateY(-10px)' },
            to: { opacity: 1, transform: 'none' },
          },
          '@keyframes heroComposerIn': {
            from: { opacity: 0, transform: 'translateY(28px) scale(.97)' },
            to: { opacity: 1, transform: 'none' },
          },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 768, py: 4 }}>
          <Typography
            component="h1"
            sx={{
              mb: 3,
              fontSize: 'clamp(1.5rem, 3.4vw, 2.25rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              animation: 'heroIn .38s cubic-bezier(.2,.9,.25,1) both',
            }}
          >
            <Trans i18nKey="chat.new.title" />
          </Typography>

          <Box sx={{ animation: 'heroComposerIn .46s cubic-bezier(.2,.9,.25,1) both' }}>
            <ChatComposer
              placement="hero"
              value={message}
              onChange={setMessage}
              onSend={() => submit(message)}
              isSubmitting={isSubmitting}
              autoFocus
              deepResearchEnabled={deepResearchEnabled}
              onDeepResearchToggle={handleToggleDeepResearch}
              shellRef={composerShellRef}
              inputRef={composerInputRef}
              attachments={attachments}
            />
          </Box>

          <Box sx={{ animation: 'heroIn .38s cubic-bezier(.2,.9,.25,1) both', animationDelay: '.1s' }}>
            {/* A suggestion is a starting point, not a decision — it lands in the
                field so the user can edit it and sends only when they say so. */}
            <SuggestedQuestions
              disabled={isSubmitting}
              onQuestionSelect={(question) => {
                setMessage(question);
                composerInputRef.current?.focus();
              }}
            />
          </Box>

          {error ? (
            <Typography sx={{ mt: 2.5, fontSize: '0.75rem', color: 'error.main' }}>
              {error}
            </Typography>
          ) : (
            <Typography
              sx={{
                mt: 2.5,
                fontSize: '0.75rem',
                color: 'text.secondary',
                animation: 'heroIn .38s cubic-bezier(.2,.9,.25,1) both',
                animationDelay: '.16s',
              }}
            >
              <Trans i18nKey="chat.new.description" />
            </Typography>
          )}
        </Box>
      </Box>
    </ChatSurface>
  );
});
